import CloseIcon from '@mui/icons-material/Close';
import Alert, { type AlertColor } from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { stateColor } from '../../../theme';
import { PartnerCockpitValidationState, type ValidationState } from '../types';

type BannerVariant = {
    severity: AlertColor;
    titleKey: string;
    descriptionKey: string;
    actionKey?: string;
    // The "action required" banner stays visible until the partner acts
    dismissible: boolean;
};

type ValidationStatusBannerState = Exclude<ValidationState, typeof PartnerCockpitValidationState.validated>;

const BANNER_VARIANTS: Record<ValidationStatusBannerState, BannerVariant> = {
    [PartnerCockpitValidationState.open]: {
        severity: 'warning',
        titleKey: 'VALIDATION_BANNER.ACTION_REQUIRED.TITLE',
        descriptionKey: 'VALIDATION_BANNER.ACTION_REQUIRED.DESCRIPTION',
        actionKey: 'VALIDATION_BANNER.ACTION_REQUIRED.ACTION',
        dismissible: false,
    },
    [PartnerCockpitValidationState.inValidation]: {
        severity: 'info',
        titleKey: 'VALIDATION_BANNER.IN_PROGRESS.TITLE',
        descriptionKey: 'VALIDATION_BANNER.IN_PROGRESS.DESCRIPTION',
        actionKey: 'VALIDATION_BANNER.IN_PROGRESS.ACTION',
        dismissible: true,
    },
    // Partner Management handed it back, so this one asks for something and stays until the craftsman resubmits.
    [PartnerCockpitValidationState.waitingOnInformation]: {
        severity: 'warning',
        titleKey: 'VALIDATION_BANNER.WAITING_ON_INFORMATION.TITLE',
        descriptionKey: 'VALIDATION_BANNER.WAITING_ON_INFORMATION.DESCRIPTION',
        actionKey: 'VALIDATION_BANNER.WAITING_ON_INFORMATION.ACTION',
        dismissible: false,
    },
    [PartnerCockpitValidationState.invalidPartner]: {
        severity: 'error',
        titleKey: 'VALIDATION_BANNER.INVALID_PARTNER.TITLE',
        descriptionKey: 'VALIDATION_BANNER.INVALID_PARTNER.DESCRIPTION',
        actionKey: 'VALIDATION_BANNER.INVALID_PARTNER.ACTION',
        dismissible: false,
    },
};

export type ValidationStatusBannerProps = {
    status: ValidationStatusBannerState;
    onAction?: () => void;
    onClose?: () => void;
};

export function ValidationStatusBanner({ status, onAction, onClose }: ValidationStatusBannerProps): ReactElement {
    const { t } = useTranslation();
    const variant = BANNER_VARIANTS[status];

    const actionLabel = variant.actionKey && onAction ? t(variant.actionKey) : undefined;
    const showClose = Boolean(variant.dismissible && onClose);

    return (
        <Alert
            severity={variant.severity}
            action={
                actionLabel || showClose ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                        {actionLabel ? (
                            <Button
                                color="inherit"
                                size="small"
                                onClick={onAction}
                                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {actionLabel}
                            </Button>
                        ) : undefined}
                        {showClose ? (
                            <IconButton
                                size="small"
                                color="inherit"
                                onClick={onClose}
                                aria-label={t('VALIDATION_BANNER.DISMISS')}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        ) : undefined}
                    </Stack>
                ) : undefined
            }
            sx={(theme) => ({
                alignItems: 'flex-start',
                border: 1,
                borderColor: stateColor(theme, 'outlinedBorder', variant.severity),
            })}>
            <AlertTitle>{t(variant.titleKey)}</AlertTitle>
            {t(variant.descriptionKey)}
        </Alert>
    );
}
